import { useLocation, useNavigate } from "react-router-dom";
import Badge from "components/UI/Badge";
import { convertEnumToText } from "utils";
import { FBDIFiltersForm } from "./FBDIFiltersForm";
import { useApplicationsListContext } from "features/ApplicationForm/Components/ApplicationsList/ApplicationsListProvider";

import { createColumnHelper } from "@tanstack/react-table";
import ReactTable from "components/UI/ReactTable";
import { ArchiveIcon } from "@heroicons/react/outline";
import Button from "components/UI/Button";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchArchiveApplication } from "api/Rover/ApplicationForm";
import Confirm from "components/UI/Modal/Confirm";
import { RoverReactQueryKey } from "constants/ReactQueryKeys";

export default function FBDIApplications() {
  const {
    data,
    tablePagination,
    showFilterModal,
    toggleFilterModal,
    applyFiltersHandler,
  } = useApplicationsListContext();

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const queryClient = useQueryClient();

  const archiveApplication = useMutation(
    () => {
      return Promise.all(
        selectedRow.map((data) =>
          patchArchiveApplication({ applicationId: data.original.id })
        )
      );
    },
    {
      onSuccess: () => {
        setShowArchiveConfirm(false);
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS]);
      },
    }
  );

  const confirmArchiveApplication = async () => {
    archiveApplication.mutate();
  };

  const cancelArchiveApplication = () => {
    setShowArchiveConfirm(false);
  };

  // const tableColumns = [
  //   {
  //     id: "howName",
  //     Header: "House of Worship Name",
  //     accessor: "applicationData",
  //     minWidth: 250,
  //     maxWidth: 250,
  //     Cell: (row: any) => {
  //       const howInfo = row.value?.howInfo;
  //       return (
  //         <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
  //           <span className="truncate">{howInfo?.name}</span>
  //           <div className="flex flex-col gap-1 text-xs text-neutralColor-normal">
  //             <span>
  //               {howInfo?.address}, {howInfo?.city}, {howInfo?.state},{" "}
  //               {howInfo?.zipCode}
  //             </span>
  //           </div>
  //         </div>
  //       );
  //     },
  //   },
  //   {
  //     id: "clergyLeader",
  //     Header: "Clergy Leader",
  //     accessor: "applicationData",
  //     minWidth: 250,
  //     maxWidth: 250,
  //     Cell: (row: any) => {
  //       const howInfo = row.value?.howInfo;
  //       return (
  //         <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
  //           <span className="truncate">{howInfo.clergyLeader}</span>
  //           <div className="flex flex-col gap-1 text-xs text-neutralColor-normal">
  //             <span>{howInfo?.phone}</span>
  //           </div>
  //         </div>
  //       );
  //     },
  //   },
  //   {
  //     id: "submitter",
  //     Header: "Submitter",
  //     accessor: "contactName",
  //     minWidth: 200,
  //     maxWidth: 200,
  //     Cell: (row: any) => {
  //       return (
  //         <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
  //           <span className="truncate">{row.value}</span>
  //           <div className="flex flex-col gap-1 text-xs text-neutralColor-normal">
  //             <span>{row?.value}</span>
  //           </div>
  //         </div>
  //       );
  //     },
  //   },
  //   {
  //     id: "projectToComplete",
  //     Header: "Project To Complete",
  //     accessor: "applicationData",
  //     minWidth: 200,
  //     maxWidth: 200,
  //     Cell: (row: any) => {
  //       const projectInfo = row.value?.projectInfo;
  //       return <span className="capitalize">{projectInfo?.q1}</span>;
  //     },
  //   },
  //   {
  //     id: "status",
  //     Header: "Status",
  //     accessor: "status",
  //     minWidth: 250,
  //     maxWidth: 250,
  //     Cell: (row: any) => {
  //       return <Badge status={row.value} text={convertEnumToText(row.value)} />;
  //     },
  //   },
  // ];

  const columnHelper = createColumnHelper<any>();
  const columns = [
    columnHelper.accessor("applicationData", {
      id: "howName",
      header: () => "House of Worship Name",
      size: 250,
      cell: (info) => {
        const howInfo = info.getValue()?.howInfo;
        return (
          <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
            <span className="truncate">{howInfo?.name}</span>
            <div className="flex flex-col gap-1 text-xs text-neutralColor-normal">
              <span>
                {howInfo?.address}, {howInfo?.city}, {howInfo?.state},{" "}
                {howInfo?.zipCode}
              </span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("applicationData", {
      id: "clergyLeader",
      header: () => "Clergy Leader",
      size: 250,
      cell: (info) => {
        const howInfo = info.getValue()?.howInfo;
        return (
          <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
            <span className="truncate">{howInfo.clergyLeader}</span>
            <div className="flex flex-col gap-1 text-xs text-neutralColor-normal">
              <span>{howInfo?.phone}</span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("contactName", {
      id: "submitter",
      header: () => "Submitter",
      size: 250,
      cell: (info) => {
        return (
          <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
            <span className="truncate">{info.getValue()}</span>
            <div className="flex flex-col gap-1 text-xs text-neutralColor-normal">
              <span>{info.getValue()}</span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("applicationData", {
      id: "projectToComplete",
      header: () => "Project To Complete",
      size: 250,
      cell: (info) => {
        const projectInfo = info.getValue().projectInfo;
        return <span className="capitalize">{projectInfo?.q1}</span>;
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => "Status",
      size: 250,
      cell: (info) => {
        return (
          <Badge
            status={info.getValue()}
            text={convertEnumToText(info.getValue())}
          />
        );
      },
    }),
  ];

  return (
    <>
      {/* <Table
        cols={tableColumns}
        tableData={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
      /> */}

      {showArchiveConfirm && (
        <Confirm
          title="Archive Application"
          message="You are about to archive selected application(s). Are you sure you want to archive ?"
          acceptButtonLabel="Archive"
          declineButtonLabel="Cancel"
          accept={confirmArchiveApplication}
          decline={cancelArchiveApplication}
        />
      )}

      {selectedRow.length > 0 && (
        <div className="absolute top-4 left-28">
          <Button
            label="Archive"
            btnStyle="neutral"
            btnSize="md"
            icon={<ArchiveIcon className="h-5 w-5" />}
            onClick={() => setShowArchiveConfirm(true)}
          />
        </div>
      )}
      <ReactTable
        cols={columns}
        data={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
        onRowSelect={(val: any[]) => setSelectedRow(val)}
      />
      {showFilterModal && (
        <FBDIFiltersForm
          handleModal={toggleFilterModal}
          handleApplyFilter={applyFiltersHandler}
        />
      )}
    </>
  );
}
